import { useGetAssignedGroupsInfinite } from '@repo/api/group/useGetAssignedGroupsInfinite';
import { useInfiniteQueryResult } from '@repo/hooks/useInfiniteQueryResult';
import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import ScheduleTimetable, { getScheduleTimetableEntries } from '@/components/features/schedule/ScheduleTimetable';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';

export default function SchedulePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const groupIdParam = searchParams.get('groupId');
  const selectedGroupId = groupIdParam ? Number(groupIdParam) : undefined;

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useGetAssignedGroupsInfinite();
  const { data: groups } = useInfiniteQueryResult(data);

  // Load every assigned group so the timetable spans all of them.
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const meetings = useMemo(() => {
    const visibleGroups = selectedGroupId === undefined ? groups : groups.filter((group) => group.id === selectedGroupId);
    return visibleGroups.flatMap(getScheduleTimetableEntries);
  }, [groups, selectedGroupId]);

  const onGroupChange = (value: string) => {
    setSearchParams(
      (prev) => {
        if (value === 'all') {
          prev.delete('groupId');
        } else {
          prev.set('groupId', value);
        }
        return prev;
      },
      { replace: true }
    );
  };

  return (
    <div className="base-direction-col">
      <div className={'border-border-primary bg-surface-primary flex w-full flex-col gap-3 rounded-2xl border p-4 sm:flex-row sm:items-center'}>
        <h2 className={'text-foreground text-xl font-semibold'}>Plan zajęć</h2>

        <Select value={selectedGroupId ? String(selectedGroupId) : 'all'} onValueChange={onGroupChange}>
          <SelectTrigger className={'w-full sm:ms-auto sm:max-w-64'}>
            <SelectValue placeholder={'Wszystkie grupy'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={'all'}>Wszystkie grupy</SelectItem>
            {groups.map((group) => (
              <SelectItem key={group.id} value={String(group.id)}>
                {group.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? <Skeleton className={'h-64 w-full rounded-2xl'} /> : <ScheduleTimetable meetings={meetings} showGroupName />}
    </div>
  );
}
